<script>
import BaseModal from '@/components/_base-modal.vue'
import BaseButton from '@/components/_base-button.vue'
import VuetifyTextClass from '@/shared/constants/core/VuetifyTextClass'
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'
import debounce from 'lodash/debounce'
import dateMixin from '@/mixins/dateMixin'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'

export default {
  name: 'BaseDatePicker',
  components: { BaseModal, BaseButton },
  mixins: [dateMixin],
  model: {
    prop: 'show',
    event: 'input',
  },
  props: {
    show: { type: Boolean, required: true },
    value: { type: [String, Array], default: '' },
    multiple: { type: Boolean, default: false },
    range: { type: Boolean, default: false },
    label: { type: String, default: 'Date' },
  },
  data() {
    return {
      internalDate: '',
      smallBody: VuetifyTextClass.smallBody,
      hasError: false,
      errorBg: VuetifyColourClass.error200,
      blueBg: VuetifyColourClass.blueBg,
      pickerFormat: DateFormatToken.YYYYMMDDWithDash,
    }
  },

  computed: {
    isOpen: {
      get() {
        return this.show
      },
      set(val) {
        this.$emit('input', val)
      },
    },

    formattedInternalDate: {
      get() {
        if (!this.isInternalDateSet) return this.initialDateValue

        if (Array.isArray(this.internalDate))
          return this.internalDate
            .map((date) => this.formatDate(date, this.localShortDateFormat))
            .join(', ')

        return this.formatDate(this.internalDate, this.localShortDateFormat)
      },

      set: debounce(function (value) {
        if (!value.trim() || !value.length) {
          this.internalDate = this.emptyPickerVal
          return
        }

        const validDates = value
          .trim()
          .split(/\s*,\s*/)
          .filter((date) => this.isValidDate(date))

        if (!validDates.length) {
          this.internalDate = this.emptyPickerVal
          this.handleError()
          return
        }

        this.internalDate = this.isMultiDate
          ? validDates.map((date) =>
              this.formatDate(date, this.pickerFormat, this.dateFormats)
            )
          : this.formatDate(validDates[0], this.pickerFormat, this.dateFormats)
      }, 300),
    },
    isOkDisabled() {
      return (
        !this.isValidDate(this.internalDate) ||
        !this.isValidDate(this.formattedInternalDate)
      )
    },

    initialDateValue() {
      return this.isMultiDate
        ? this.initialArrayDateValue
        : this.initialStringDateValue
    },
    initialArrayDateValue() {
      return this.isInternalDateSet
        ? this.internalDate
            .map((date) => this.formatDate(date, this.textFieldDateFormat))
            .join(', ')
        : ['']
    },
    initialStringDateValue() {
      return this.isInternalDateSet
        ? this.formatDate(this.value, this.textFieldDateFormat)
        : ''
    },
    isMultiDate() {
      return this.range || this.multiple || Array.isArray(this.value)
    },
    emptyPickerVal() {
      return this.isMultiDate ? [] : ''
    },
    textFieldClasses() {
      return [
        { 'v-input--is-focused': this.isInternalDateSet },
        { 'show-text-error': this.hasError },
        this.hasError ? 'error--text' : 'primary--text',
      ]
    },
    textFieldBackground() {
      return this.hasError ? this.errorBg : this.blueBg
    },
    isInternalDateSet() {
      return !!this.internalDate?.length
    },
  },

  mounted() {
    if (!this.isInternalDateSet) {
      this.internalDate = this.value ?? this.emptyPickerVal
    }
  },
  methods: {
    handleError() {
      this.hasError = true

      setTimeout(() => {
        this.hasError = false
      }, 800)
    },
    close() {
      this.isOpen = false
    },
    confirm() {
      const isValid = this.isMultiDate
        ? this.internalDate.every(this.isValidDate)
        : this.isValidDate(this.internalDate)

      if (!isValid) {
        this.handleError()
        return
      }

      this.$emit('confirm', this.internalDate)
      this.close()
    },
  },
}
</script>

<template>
  <BaseModal
    v-model="isOpen"
    width="280"
    persistent
    hide-cancel
    hide-padding
    border-radius="8px"
  >
    <template v-slot:header>
      <v-text-field
        v-model="formattedInternalDate"
        :label="label"
        outlined
        filled
        dense
        hide-details
        :clearable="isMultiDate"
        :readonly="isMultiDate"
        :class="textFieldClasses"
        :background-color="textFieldBackground"
        class="date-picker-text-field text-wrap"
      ></v-text-field>
    </template>

    <v-date-picker
      v-model="internalDate"
      v-bind="$attrs"
      class="my-2 date-picker"
      no-title
      full-width
      color="primary"
      :class="smallBody"
      :multiple="multiple"
      :range="range"
    />

    <template v-slot:actions>
      <BaseButton tertiary-blue sm width="79" @click="close">{{
        $t('general.cancel')
      }}</BaseButton>
      <BaseButton
        tertiary-blue
        sm
        width="47"
        :disabled="isOkDisabled"
        @click="confirm"
        >{{ $t('general.ok') }}</BaseButton
      >
    </template>
  </BaseModal>
</template>

<style scoped lang="scss">
@import '@/styles/_colors.scss';
@import '@/styles/_animations.scss';
.date-picker-text-field {
  font-weight: normal !important;
}

.date-picker ::v-deep .v-date-picker-header__value button {
  font-weight: normal !important;
}

.date-picker
  ::v-deep
  .v-date-picker-table.v-date-picker-table--date
  .v-btn.v-btn--outlined {
  color: $color-white !important;
  background: $color-blue-400 !important;
}

.date-picker
  ::v-deep
  .v-date-picker-table.v-date-picker-table--date
  .v-btn.v-btn--active {
  color: $color-blue-400 !important;
  background: $color-blue-bg !important;
}

.show-text-error {
  background-color: $color-error-200 !important;
  animation: shake-left-right 0.4s ease-in-out;
}

.show-text-error ::v-deep .v-label {
  color: $color-error-400 !important;
}
</style>
